/**
 * One step in the builder's list: its settings, in the shape the server
 * stores (`WorkflowStep.config`, see workflowModel.ts), and the controls
 * to move it up or down or take it out.
 *
 * Steps are a tree: an "If / else" step holds a Yes list and a No list,
 * drawn indented under it ([`StepList`]), each with its own Add step. A
 * branch can hold branches, at most three deep; the Add step menu stops
 * offering one at the limit. A "Repeat" step holds the steps it does each
 * time round, drawn the same way; it counts as a level, and holds no other
 * Repeat.
 */
import { Fragment, type ReactNode } from 'react';
import {
  ActionIcon, Alert, Badge, Box, Button, Card, Code, CopyButton, Group, Menu, NumberInput, SegmentedControl, Select,
  Stack, Switch, Text, TextInput, Textarea, ThemeIcon, Tooltip,
} from '@mantine/core';
import {
  IconArrowDown, IconArrowUp, IconBell, IconCheck, IconChecklist, IconClock, IconCopy, IconGitBranch, IconHourglass,
  IconMail, IconMessage, IconPencil, IconPlus, IconTrash, IconUserCheck, IconArrowRight, IconWebhook, IconArrowBackUp,
  IconCircleX, IconRepeat,
} from '@tabler/icons-react';
import { useAssignmentGroups } from '../../api/assignment';
import { usePipelines } from '../../api/crm';
import { useProperties, type PropertyObject } from '../../api/properties';
import { useParams } from 'react-router-dom';
import { useEmailBrands, useEmailTemplates } from '../../api/queries';
import {
  useWorkflowSendBudget, type RecordType, type StepKind, type WorkflowListResponse, type WorkflowStep, type WorkflowTrigger,
} from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab } from '../../vocab';
import { useOwnerOptions } from '../crm/crmShared';
import { fromInput, PropertyInput, toInput } from '../properties/PropertyFields';
import { ConditionsEditor } from './ConditionsCard';
import { EventPicker } from './TriggerCard';
import {
  cfg, DEFAULT_GO_TO_TIMES, firstSendId, isEmailEvent, MAX_GO_TO_TIMES, MAX_REPEAT_TIMES, MIN_REPEAT_TIMES, num,
  recordWordA, stepLabel, stepsOffered, stepWorksOn, str, UNITS, waitSentence, waitUntilSentence, type ListRef,
} from './workflowModel';

/** What the builder does to the tree, by step id. */
export type TreeOps = {
  change: (id: string, next: WorkflowStep) => void;
  move: (id: string, by: -1 | 1) => void;
  remove: (id: string) => void;
  add: (at: ListRef, kind: StepKind) => void;
};

/** What every step in the tree shares. */
export type TreeContext = {
  rt: RecordType;
  canManage: boolean;
  smsAvailable: boolean;
  webhookSecret?: string | null;
  list: WorkflowListResponse | undefined;
  /** A step's number, counting every step in written order. */
  numbers: Map<string, number>;
  /** Every step, those inside branches too, in written order (for a Go to's target). */
  all: WorkflowStep[];
  ops: TreeOps;
};

/** The line between cards, so the list reads as one thing after another. */
export function Connector({ label }: { label?: string }) {
  return (
    <Group gap={8} wrap="nowrap" h={label ? 30 : 18} pl={22} align="center">
      <div style={{ width: 2, alignSelf: 'stretch', background: TOKENS.borderStrong, borderRadius: 1 }} />
      {label && <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>{label}</Text>}
    </Group>
  );
}

/** One list of steps: the top of the workflow, one side of a branch, or a repeat's steps. */
export function StepList({
  steps, at, depth, ctx, firstLabel, inRepeat = false,
}: {
  steps: WorkflowStep[];
  at: ListRef;
  /** How many branches and repeats hold this list. */
  depth: number;
  ctx: TreeContext;
  /** Over the first step: "Then". */
  firstLabel?: string;
  /** Inside a repeat, where another repeat may not go. */
  inRepeat?: boolean;
}) {
  const v = useVocab();
  const offered = stepsOffered(ctx.rt, ctx.smsAvailable, depth, inRepeat);
  const testId = at.parentId ? `add-step-${at.parentId}-${at.side}` : 'add-step';
  return (
    <Stack gap={0}>
      {steps.map((s, i) => (
        <Fragment key={s.id}>
          <Connector label={i === 0 ? firstLabel : undefined} />
          <StepCard
            step={s}
            index={i}
            count={steps.length}
            depth={depth}
            ctx={ctx}
            inRepeat={inRepeat}
          />
        </Fragment>
      ))}
      {ctx.canManage && (
        <>
          <Connector />
          <Menu position="bottom-start" withinPortal>
            <Menu.Target>
              <Button
                variant="light"
                size={depth ? 'xs' : 'sm'}
                leftSection={<IconPlus size={depth ? 14 : 16} />}
                style={{ alignSelf: 'flex-start' }}
                data-testid={testId}
              >
                Add step
              </Button>
            </Menu.Target>
            <Menu.Dropdown data-testid="step-menu">
              <Menu.Label>{`Steps that act on ${recordWordA(v, ctx.rt)}`}</Menu.Label>
              {offered.map((k) => {
                const Icon = STEP_ICONS[k];
                return (
                  <Menu.Item key={k} leftSection={<Icon size={15} />} onClick={() => ctx.ops.add(at, k)}>
                    {stepLabel(v, k)}
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        </>
      )}
      {steps.length === 0 && depth > 0 && !ctx.canManage && (
        <Text size="xs" c="dimmed" mt={4}>Nothing: it goes straight on.</Text>
      )}
    </Stack>
  );
}

export const STEP_ICONS: Record<StepKind, typeof IconPencil> = {
  set_property: IconPencil,
  assign_owner: IconUserCheck,
  create_task: IconChecklist,
  send_email: IconMail,
  send_sms: IconMessage,
  notify: IconBell,
  move_stage: IconArrowRight,
  webhook: IconWebhook,
  wait: IconClock,
  wait_until: IconHourglass,
  branch: IconGitBranch,
  go_to: IconArrowBackUp,
  end: IconCircleX,
  repeat: IconRepeat,
};

type Config = Record<string, unknown>;

export function StepCard({
  step, index, count, depth, ctx, inRepeat = false,
}: {
  step: WorkflowStep;
  /** Its place in its own list, for moving it. */
  index: number;
  count: number;
  depth: number;
  ctx: TreeContext;
  /** Inside a repeat's steps. */
  inRepeat?: boolean;
}) {
  const v = useVocab();
  const { rt, canManage, webhookSecret } = ctx;
  const Icon = STEP_ICONS[step.kind] ?? IconPencil;
  const c = cfg(step);
  const onChange = (next: WorkflowStep) => ctx.ops.change(step.id, next);
  const onMove = (by: -1 | 1) => ctx.ops.move(step.id, by);
  const onRemove = () => ctx.ops.remove(step.id);
  const set = (patch: Config) => onChange({ ...step, config: { ...c, ...patch } });
  const fits = stepWorksOn(step.kind, rt);
  const label = stepLabel(v, step.kind);
  const number = ctx.numbers.get(step.id) ?? index + 1;
  const title = step.kind === 'wait'
    ? waitSentence(c)
    : step.kind === 'wait_until'
      ? waitUntilSentence(v, c)
      : step.kind === 'go_to'
        ? goToSentence(c, ctx.numbers)
        : step.kind === 'end'
          ? (c.outcome === 'exited' ? 'End here, as exited' : 'End here')
          : step.kind === 'repeat'
            ? repeatSentence(c)
            : label;

  return (
    <Card padding="sm" withBorder data-testid={`step-${number - 1}`} data-kind={step.kind} data-step-id={step.id}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <ThemeIcon variant="light" radius="xl" size={28} color="chartBlue" style={{ flex: 'none' }}>
              <Icon size={15} />
            </ThemeIcon>
            <Text size="xs" c="dimmed" style={{ flex: 'none' }}>{`Step ${number}`}</Text>
            <Text
              size="sm"
              fw={600}
              lineClamp={2}
              data-testid={step.kind === 'wait' ? 'wait-sentence' : step.kind === 'wait_until' ? 'wait-until-sentence' : undefined}
            >
              {title}
            </Text>
          </Group>
          {canManage && (
            <Group gap={2} wrap="nowrap" style={{ flex: 'none' }}>
              <Tooltip label="Move up" openDelay={400}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Move step ${number} up`}
                  disabled={index === 0}
                  onClick={() => onMove(-1)}
                >
                  <IconArrowUp size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Move down" openDelay={400}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Move step ${number} down`}
                  disabled={index === count - 1}
                  onClick={() => onMove(1)}
                >
                  <IconArrowDown size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Remove" openDelay={400}>
                <ActionIcon variant="subtle" color="red" aria-label={`Remove step ${number}`} onClick={onRemove}>
                  <IconTrash size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>
        {!fits && (
          <Alert color="red" variant="light" p="xs">
            <Text size="sm">{`This step cannot act on ${recordWordA(v, rt)}. Remove it or choose another trigger.`}</Text>
          </Alert>
        )}
        <StepSettings step={step} c={c} set={set} rt={rt} webhookSecret={webhookSecret} ctx={ctx} />
        {step.kind === 'branch' && (
          <Stack gap="sm" data-testid={`branch-${step.id}`}>
            {(['yes', 'no'] as const).map((side) => (
              <Box
                key={side}
                pl="sm"
                style={{
                  borderLeft: `3px solid ${side === 'yes' ? 'var(--mantine-color-chartGreen-5)' : TOKENS.borderStrong}`,
                }}
              >
                <Badge
                  variant="light"
                  color={side === 'yes' ? 'chartGreen' : 'gray'}
                  radius="sm"
                  tt="none"
                  data-testid={`branch-${step.id}-${side}`}
                >
                  {side === 'yes' ? 'Yes' : 'No'}
                </Badge>
                <StepList
                  steps={step[side] ?? []}
                  at={{ parentId: step.id, side }}
                  depth={depth + 1}
                  ctx={ctx}
                  inRepeat={inRepeat}
                />
              </Box>
            ))}
          </Stack>
        )}
        {step.kind === 'repeat' && (
          <Box
            pl="sm"
            data-testid={`repeat-${step.id}`}
            style={{ borderLeft: '3px solid var(--mantine-color-chartBlue-5)' }}
          >
            <Badge variant="light" color="chartBlue" radius="sm" tt="none">Each time round</Badge>
            <StepList
              steps={step.body ?? []}
              at={{ parentId: step.id, side: 'body' }}
              depth={depth + 1}
              ctx={ctx}
              inRepeat
            />
          </Box>
        )}
      </Stack>
    </Card>
  );
}

export function StepSettings({
  step, c, set, rt, webhookSecret, ctx,
}: {
  step: WorkflowStep;
  c: Config;
  set: (patch: Config) => void;
  rt: RecordType;
  webhookSecret?: string | null;
  ctx: TreeContext;
}) {
  const list = ctx.list;
  switch (step.kind) {
    case 'set_property': return <SetPropertyForm c={c} set={set} rt={rt} />;
    case 'assign_owner': return <AssignOwnerForm c={c} set={set} rt={rt} />;
    case 'create_task': return <CreateTaskForm c={c} set={set} />;
    case 'send_email': return <SendEmailForm c={c} set={set} first={firstSendId(ctx.all) === step.id} />;
    case 'send_sms':
      return (
        <Textarea
          size="xs"
          label="Message"
          autosize
          minRows={2}
          value={str(c.message)}
          onChange={(e) => set({ message: e.currentTarget.value })}
        />
      );
    case 'notify': return <NotifyForm c={c} set={set} />;
    case 'move_stage': return <MoveStageForm c={c} set={set} />;
    case 'webhook': return <WebhookForm c={c} set={set} secret={webhookSecret} />;
    case 'wait': return <WaitForm c={c} set={set} />;
    case 'wait_until': return <WaitUntilForm c={c} set={set} rt={rt} list={list} ctx={ctx} />;
    case 'branch': return <BranchForm c={c} set={set} rt={rt} list={list} />;
    case 'go_to': return <GoToForm c={c} set={set} self={step.id} ctx={ctx} />;
    case 'end': return <EndForm c={c} set={set} />;
    case 'repeat': return <RepeatForm c={c} set={set} />;
  }
}

/** A Repeat in words: "Do these steps 3 times". */
function repeatSentence(c: Config): string {
  return `Do these steps ${Math.trunc(num(c.times, MIN_REPEAT_TIMES))} times`;
}

/** How many times round: the number of passes in all. */
function RepeatForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="nowrap" align="center">
        <Text size="sm">Do these steps</Text>
        <NumberInput
          size="xs"
          aria-label="How many times"
          min={MIN_REPEAT_TIMES}
          max={MAX_REPEAT_TIMES}
          allowDecimal={false}
          clampBehavior="strict"
          value={num(c.times, MIN_REPEAT_TIMES)}
          onChange={(n) => set({ times: typeof n === 'number' ? n : MIN_REPEAT_TIMES })}
          w={80}
          data-testid="repeat-times"
        />
        <Text size="sm">times</Text>
      </Group>
      <Text size="xs" c="dimmed">
        {`Between ${MIN_REPEAT_TIMES} and ${MAX_REPEAT_TIMES} times, then it carries on with the step after it. Every way round needs a Wait or Wait until step in it, so it never repeats instantly.`}
      </Text>
    </Stack>
  );
}

/** A Go to in words: "Go back to step 2, up to 3 times". */
function goToSentence(c: Config, numbers: Map<string, number>): string {
  const n = numbers.get(str(c.target));
  const times = Math.trunc(num(c.max_times, DEFAULT_GO_TO_TIMES));
  const upTo = times === 1 ? 'once' : `up to ${times} times`;
  return n ? `Go back to step ${n}, ${upTo}` : 'Go to';
}

/** Go back (or on) to another step, at most so many times, then carry on. */
function GoToForm({ c, set, self, ctx }: { c: Config; set: (p: Config) => void; self: string; ctx: TreeContext }) {
  const v = useVocab();
  const options = ctx.all
    .filter((s) => s.id !== self)
    .map((s) => ({ value: s.id, label: `Step ${ctx.numbers.get(s.id) ?? '?'}: ${stepLabel(v, s.kind)}` }));
  return (
    <Stack gap="xs">
      <Row>
        <Select
          size="xs"
          label="Go back to"
          placeholder="Choose a step"
          data={options}
          value={str(c.target) || null}
          onChange={(x) => set({ target: x ?? '' })}
          style={{ flex: '1 1 220px' }}
          data-testid="go-to-target"
        />
        <NumberInput
          size="xs"
          label="At most"
          min={1}
          max={MAX_GO_TO_TIMES}
          allowDecimal={false}
          value={num(c.max_times, DEFAULT_GO_TO_TIMES)}
          onChange={(n) => set({ max_times: typeof n === 'number' ? n : DEFAULT_GO_TO_TIMES })}
          suffix=" times"
          w={120}
          data-testid="go-to-times"
        />
      </Row>
      <Text size="xs" c="dimmed">
        After that many times round it carries on with the step after this one. Every way round needs a Wait or Wait until
        step in it, so it never repeats instantly.
      </Text>
    </Stack>
  );
}

/** Stop here: done, or exited with a reason. */
function EndForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  const outcome = c.outcome === 'exited' ? 'exited' : 'done';
  return (
    <Stack gap="xs">
      <SegmentedControl
        size="xs"
        data={[{ value: 'done', label: 'Done' }, { value: 'exited', label: 'Exited' }]}
        value={outcome}
        onChange={(x) => set({ outcome: x })}
        style={{ alignSelf: 'flex-start' }}
        data-testid="end-outcome"
      />
      <TextInput
        size="xs"
        label="Reason"
        description="Optional. Shown on the run."
        maxLength={200}
        value={str(c.reason)}
        onChange={(e) => set({ reason: e.currentTarget.value || undefined })}
        data-testid="end-reason"
      />
      <Text size="xs" c="dimmed">
        {outcome === 'done'
          ? 'The run finishes here. Nothing after this step on this path runs.'
          : 'The run stops here and counts as exited, as an exit rule would. Nothing after it runs.'}
      </Text>
    </Stack>
  );
}

function TimeLimit({ c, set }: { c: Config; set: (p: Config) => void }) {
  return (
    <Row>
      <NumberInput
        size="xs"
        label="Or at most"
        min={1}
        allowDecimal={false}
        value={num(c.amount)}
        onChange={(n) => set({ amount: typeof n === 'number' ? n : 0 })}
        w={110}
        data-testid="wait-until-amount"
      />
      <Select
        size="xs"
        label=" "
        aria-label="Unit"
        data={UNITS}
        value={str(c.unit) || 'days'}
        allowDeselect={false}
        onChange={(x) => x && set({ unit: x })}
        w={120}
      />
    </Row>
  );
}

/** Wait until an event or conditions, or a time limit. */
function WaitUntilForm({
  c, set, rt, list, ctx,
}: { c: Config; set: (p: Config) => void; rt: RecordType; list: WorkflowListResponse | undefined; ctx: TreeContext }) {
  const mode = c.event ? 'event' : 'conditions';
  return (
    <Stack gap="xs">
      <SegmentedControl
        size="xs"
        data={[{ value: 'event', label: 'Something happens' }, { value: 'conditions', label: 'Something becomes true' }]}
        value={mode}
        onChange={(m) => set(m === 'event'
          ? { event: { kind: 'email_link_clicked' }, conditions: undefined }
          : { event: undefined, conditions: [], email_from: undefined })}
        style={{ alignSelf: 'flex-start' }}
        data-testid="wait-until-mode"
      />
      {mode === 'event' ? (
        <>
          <EventPicker
            event={c.event as WorkflowTrigger}
            onChange={(event) => set(isEmailEvent(event) ? { event } : { event, email_from: undefined })}
            label="Until"
            testId="wait-until-event"
          />
          {isEmailEvent(c.event) && <EmailFromPicker c={c} set={set} ctx={ctx} />}
        </>
      ) : (
        <ConditionsEditor rt={rt} conditions={c.conditions} onChange={(conditions) => set({ conditions })} list={list} />
      )}
      <TimeLimit c={c} set={set} />
      <Text size="xs" c="dimmed">
        {mode === 'event'
          ? 'It goes on as soon as that happens to this record, or when the time is up. A branch after it can tell which.'
          : 'Checked whenever the record changes, and at least hourly, until the time is up. A branch after it can tell which.'}
      </Text>
    </Stack>
  );
}

/**
 * Whose email a wait on an email event listens to: any email to the
 * record, only this run's, or only the email one step of this run sent.
 */
function EmailFromPicker({ c, set, ctx }: { c: Config; set: (p: Config) => void; ctx: TreeContext }) {
  const v = useVocab();
  const sends = ctx.all
    .filter((s) => s.kind === 'send_email')
    .map((s) => ({ value: s.id, label: `Only the email from step ${ctx.numbers.get(s.id) ?? '?'}` }));
  const raw = str(c.email_from);
  const value = raw || 'any';
  const known = value === 'any' || value === 'this_run' || sends.some((s) => s.value === value);
  return (
    <Select
      size="xs"
      label="Which email"
      data={[
        { value: 'any', label: 'Any email to them' },
        { value: 'this_run', label: "Only this run's emails" },
        ...sends,
        ...(known ? [] : [{ value, label: 'An email step that has been removed' }]),
      ]}
      value={value}
      allowDeselect={false}
      onChange={(x) => set({ email_from: !x || x === 'any' ? undefined : x })}
      description={`Only this run's: a click on an older email, or one from a campaign or another ${v.one('workflow')}, does not count.`}
      w={300}
      data-testid="wait-until-email-from"
    />
  );
}

/** If the record matches (and the last wait went one way), yes; otherwise no. */
function BranchForm({ c, set, rt, list }: { c: Config; set: (p: Config) => void; rt: RecordType; list: WorkflowListResponse | undefined }) {
  const wait = c.wait_matched === true ? 'matched' : c.wait_matched === false ? 'timed_out' : 'any';
  return (
    <Stack gap="xs">
      <Select
        size="xs"
        label="If the last wait"
        data={[
          { value: 'any', label: 'Do not look at the last wait' },
          { value: 'matched', label: 'Matched (it happened)' },
          { value: 'timed_out', label: 'Timed out' },
        ]}
        value={wait}
        allowDeselect={false}
        onChange={(x) => set({ wait_matched: x === 'matched' ? true : x === 'timed_out' ? false : undefined })}
        w={280}
        data-testid="branch-wait"
      />
      <Text size="xs" fw={500}>And the record matches</Text>
      <ConditionsEditor rt={rt} conditions={c.conditions} onChange={(conditions) => set({ conditions })} list={list} />
      <Text size="xs" c="dimmed">Yes when all of that holds; otherwise No. Both paths go on to the step after this one.</Text>
    </Stack>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <Group gap="xs" align="flex-end" wrap="wrap" style={{ rowGap: 8 }}>{children}</Group>;
}

const PLACEHOLDERS = 'Use {{name}}, {{email}} and {{owner}} to fill in from the record.';

function SetPropertyForm({ c, set, rt }: { c: Config; set: (p: Config) => void; rt: RecordType }) {
  const v = useVocab();
  const object = (str(c.object) || 'person') as PropertyObject;
  const defs = useProperties(object).data?.properties ?? [];
  const def = defs.find((d) => d.key === str(c.key)) ?? null;
  // Only a deal workflow can set a deal or company property.
  const objects = [
    { value: 'person', label: v.One('person') },
    ...(rt === 'deal' ? [{ value: 'deal', label: v.One('deal') }, { value: 'company', label: `The ${v.one('deal')}'s ${v.one('company')}` }] : []),
  ];
  return (
    <Stack gap="xs">
      <Row>
        <Select
          size="xs"
          label="On"
          data={objects}
          value={object}
          allowDeselect={false}
          onChange={(x) => x && set({ object: x, key: '', value: '' })}
          w={200}
        />
        <Select
          size="xs"
          label={v.One('property')}
          placeholder={`Choose a ${v.one('property')}`}
          searchable
          data={defs.map((d) => ({ value: d.key, label: d.label }))}
          value={str(c.key) || null}
          onChange={(x) => set({ key: x ?? '', value: '' })}
          nothingFoundMessage={`No ${v.many('property')}`}
          style={{ flex: '1 1 200px' }}
          data-testid="set-property-key"
        />
      </Row>
      {def && (
        <PropertyInput
          def={{ ...def, label: 'Set it to', description: 'Left empty, the value is cleared.', required: false }}
          value={toInput(def, c.value)}
          onChange={(x) => set({ value: fromInput(def, x) ?? '' })}
        />
      )}
    </Stack>
  );
}

function AssignOwnerForm({ c, set, rt }: { c: Config; set: (p: Config) => void; rt: RecordType }) {
  const v = useVocab();
  const owners = useOwnerOptions();
  const groups = useAssignmentGroups(rt !== 'deal').data?.groups ?? [];
  const mode = rt === 'deal' ? 'user' : (str(c.mode) || 'rotation');
  return (
    <Stack gap="xs">
      {rt !== 'deal' && (
        <SegmentedControl
          size="xs"
          data={[{ value: 'rotation', label: 'Through a rotation' }, { value: 'user', label: 'To one person' }]}
          value={mode}
          onChange={(m) => set(m === 'rotation' ? { mode: m, group_id: '', user_id: undefined } : { mode: m, user_id: '', group_id: undefined })}
          style={{ alignSelf: 'flex-start' }}
        />
      )}
      {mode === 'rotation' ? (
        <Select
          size="xs"
          label="Rotation"
          description="The next rep in it, its caps and hours respected."
          placeholder="Choose a rotation"
          data={groups.map((g) => ({ value: g.id, label: g.name }))}
          value={str(c.group_id) || null}
          onChange={(x) => set({ group_id: x ?? '' })}
          nothingFoundMessage="No rotations yet. Make one in Settings, Teams."
          data-testid="assign-rotation"
        />
      ) : (
        <Select
          size="xs"
          label={v.One('owner')}
          placeholder="Choose who"
          searchable
          data={owners}
          value={str(c.user_id) || null}
          onChange={(x) => set({ mode: 'user', user_id: x ?? '', group_id: undefined })}
        />
      )}
    </Stack>
  );
}

function DueIn({ c, set, label }: { c: Config; set: (p: Config) => void; label: string }) {
  return (
    <Row>
      <NumberInput
        size="xs"
        label={label}
        min={0}
        allowDecimal={false}
        value={num(c.due_amount)}
        onChange={(n) => set({ due_amount: typeof n === 'number' ? n : 0 })}
        w={110}
      />
      <Select
        size="xs"
        label=" "
        aria-label="Unit"
        data={UNITS}
        value={str(c.due_unit) || 'minutes'}
        allowDeselect={false}
        onChange={(x) => x && set({ due_unit: x })}
        w={120}
      />
    </Row>
  );
}

function CreateTaskForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  const v = useVocab();
  const owners = useOwnerOptions();
  const owner = str(c.owner) || 'record_owner';
  return (
    <Stack gap="xs">
      <TextInput
        size="xs"
        label="Subject"
        description={PLACEHOLDERS}
        value={str(c.subject)}
        onChange={(e) => set({ subject: e.currentTarget.value })}
        data-testid="task-subject"
      />
      <Textarea
        size="xs"
        label="Notes"
        autosize
        minRows={1}
        value={str(c.body)}
        onChange={(e) => set({ body: e.currentTarget.value })}
      />
      <Row>
        <DueIn c={c} set={set} label="Due in" />
        <Select
          size="xs"
          label="For"
          data={[{ value: 'record_owner', label: `The record's ${v.one('owner')}` }, { value: 'user', label: 'One person' }]}
          value={owner}
          allowDeselect={false}
          onChange={(x) => x && set(x === 'user' ? { owner: x, user_id: '' } : { owner: x, user_id: undefined })}
          w={190}
        />
        {owner === 'user' && (
          <Select
            size="xs"
            label="Who"
            searchable
            placeholder="Choose who"
            data={owners}
            value={str(c.user_id) || null}
            onChange={(x) => set({ user_id: x ?? '' })}
            style={{ flex: '1 1 180px' }}
          />
        )}
      </Row>
    </Stack>
  );
}

/** The longest a send step may wait to go out, in hours (the server's limit). */
const MAX_AGE_HOURS = 168;

function SendEmailForm({ c, set, first }: { c: Config; set: (p: Config) => void; first: boolean }) {
  const v = useVocab();
  const templates = useEmailTemplates().data?.templates ?? [];
  const brands = useEmailBrands().data?.brands ?? [];
  // The builder's own route says which workflow this is (none while new).
  const { id: routeId } = useParams();
  const workflowId = routeId && routeId !== 'new' ? routeId : null;
  const budget = useWorkflowSendBudget(str(c.brand_id) || null, str(c.template_id) || null, workflowId).data;
  return (
    <Stack gap="xs">
      <Row>
        <Select
          size="xs"
          label="Template"
          placeholder="Choose a template"
          searchable
          data={templates.map((t) => ({ value: t.id, label: t.name }))}
          value={str(c.template_id) || null}
          onChange={(x) => set({ template_id: x ?? '' })}
          style={{ flex: '1 1 220px' }}
        />
        <Select
          size="xs"
          label="Brand"
          placeholder="The default brand"
          clearable
          data={brands.map((b) => ({ value: b.id, label: b.name }))}
          value={str(c.brand_id) || null}
          onChange={(x) => set({ brand_id: x ?? undefined })}
          style={{ flex: '1 1 180px' }}
        />
      </Row>
      <Switch
        size="xs"
        label="Once per person"
        description={first
          ? `Never email the same person from this ${v.one('workflow')} twice. On by default for the first email step.`
          : `Skip this email if this ${v.one('workflow')} has already emailed them. Off by default after the first email step.`}
        checked={typeof c.once_per_person === 'boolean' ? c.once_per_person : first}
        onChange={(e) => set({ once_per_person: e.currentTarget.checked })}
        data-testid="once-per-person"
      />
      {(typeof c.once_per_person === 'boolean' ? c.once_per_person : first) && (
        <Text size="xs" c="dimmed" mt={-4} data-testid="send-again-note">
          {`Each person gets this email once from this ${v.one('workflow')}. To send it again, duplicate the ${v.one('workflow')}.`}
        </Text>
      )}
      <Switch
        size="xs"
        label="Stop here if not sent"
        description={`If this email is not sent (already emailed, no email consent, on the never-email list, or too late), the person leaves the ${v.one('workflow')} here instead of going on to the steps after it.`}
        checked={c.stop_if_not_sent === true}
        onChange={(e) => set({ stop_if_not_sent: e.currentTarget.checked || undefined })}
        data-testid="stop-if-not-sent"
      />
      <Group gap={6} align="center" wrap="wrap">
        <Text size="xs">Give up if not sent within</Text>
        <NumberInput
          size="xs"
          aria-label="Give up if not sent within this many hours"
          placeholder="Never"
          min={1}
          max={MAX_AGE_HOURS}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          value={typeof c.max_age_hours === 'number' ? c.max_age_hours : ''}
          onChange={(n) => set({ max_age_hours: typeof n === 'number' && n > 0 ? n : undefined })}
          w={80}
          data-testid="max-age-hours"
        />
        <Text size="xs">hours (optional)</Text>
      </Group>
      <Text size="xs" c="dimmed" mt={-4}>
        Counted from when they entered this automation (for a new arrival, when they arrived). An email held back by a daily limit gives up the same way, so it never goes out late.
      </Text>
      {budget?.sentence && (
        <Text
          size="xs"
          c={budget.left_today === 0 ? 'orange' : 'dimmed'}
          data-testid="send-budget"
        >
          {budget.left_today === 0
            ? `${budget.sentence} Emails from here wait until 08:00 tomorrow.`
            : budget.sentence}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        Sent through Amazon SES: only to those opted in, and never to anyone on the never-email list.
      </Text>
    </Stack>
  );
}

function NotifyForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  const v = useVocab();
  const owners = useOwnerOptions();
  const to = str(c.to) || 'owner';
  return (
    <Stack gap="xs">
      <Row>
        <Select
          size="xs"
          label="Who"
          data={[
            { value: 'owner', label: `The record's ${v.one('owner')}` },
            { value: 'team_lead', label: `Their ${v.one('team')}'s leader` },
            { value: 'user', label: 'One person' },
          ]}
          value={to}
          allowDeselect={false}
          onChange={(x) => x && set(x === 'user' ? { to: x, user_id: '' } : { to: x, user_id: undefined })}
          w={190}
        />
        {to === 'user' && (
          <Select
            size="xs"
            label="Person"
            searchable
            placeholder="Choose who"
            data={owners}
            value={str(c.user_id) || null}
            onChange={(x) => set({ user_id: x ?? '' })}
            style={{ flex: '1 1 180px' }}
          />
        )}
      </Row>
      <TextInput
        size="xs"
        label="Title"
        description={PLACEHOLDERS}
        value={str(c.title)}
        onChange={(e) => set({ title: e.currentTarget.value })}
      />
      <Textarea
        size="xs"
        label="Words"
        autosize
        minRows={1}
        value={str(c.body)}
        onChange={(e) => set({ body: e.currentTarget.value })}
      />
      <Text size="xs" c="dimmed">A push to their phone and a notification in the app.</Text>
    </Stack>
  );
}

function MoveStageForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  const v = useVocab();
  const pipelines = (usePipelines().data?.pipelines ?? []).filter((p) => !p.archived);
  const stages = pipelines.flatMap((p) => p.stages.filter((s) => !s.archived).map((s) => ({ ...s, pipeline: p.name })));
  const chosen = stages.find((s) => s.id === str(c.stage_id));
  const lost = chosen?.kind === 'lost';
  return (
    <Stack gap="xs">
      <Select
        size="xs"
        label={`Move to`}
        placeholder={`Choose a ${v.one('stage')}`}
        searchable
        data={pipelines.map((p) => ({
          group: p.name,
          items: p.stages.filter((s) => !s.archived).map((s) => ({
            value: s.id,
            label: s.kind === 'open' ? s.name : `${s.name} (${s.kind})`,
          })),
        }))}
        value={str(c.stage_id) || null}
        onChange={(x) => set({ stage_id: x ?? '' })}
        data-testid="move-stage"
      />
      {lost && (
        <TextInput
          size="xs"
          label="Reason"
          description={`A lost ${v.one('deal')} needs a reason.`}
          required
          value={str(c.reason)}
          onChange={(e) => set({ reason: e.currentTarget.value })}
          error={!str(c.reason).trim() ? 'Needed for a lost stage' : undefined}
          data-testid="lost-reason"
        />
      )}
      <Text size="xs" c="dimmed">{`Written to the ${v.one('deal')}'s ${v.one('stage')} history, as a move by hand is.`}</Text>
    </Stack>
  );
}

function WebhookForm({ c, set, secret }: { c: Config; set: (p: Config) => void; secret?: string | null }) {
  const v = useVocab();
  return (
    <Stack gap="xs">
      <TextInput
        size="xs"
        label="Address"
        placeholder="https://example.com/hooks/hawfinch"
        value={str(c.url)}
        onChange={(e) => set({ url: e.currentTarget.value })}
        data-testid="webhook-url"
      />
      <Text size="xs" c="dimmed">
        {`The record is sent as JSON in a POST. A call that does not get through is tried again; if it never does, the run fails and says why.`}
      </Text>
      <Card padding="xs" radius="sm" style={{ background: TOKENS.surface2 }}>
        <Stack gap={6}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Text size="xs" fw={600}>Signing secret</Text>
            {secret && (
              <CopyButton value={secret}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                    <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy} aria-label="Copy signing secret">
                      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
          </Group>
          {secret ? (
            <Code block style={{ fontSize: 11, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }} data-testid="webhook-secret">{secret}</Code>
          ) : (
            <Text size="xs" c="dimmed">{`Shown once the ${v.one('workflow')} is saved, to those who may manage ${v.many('workflow')}.`}</Text>
          )}
          <Text size="xs" style={{ color: TOKENS.textSecondary }}>
            Each call carries <Code>X-Hawfinch-Timestamp</Code> (Unix seconds), <Code>X-Hawfinch-Delivery</Code> (the same on a retry, so
            you can ignore repeats) and <Code>X-Hawfinch-Signature</Code>: <Code>sha256=</Code> and the hex HMAC-SHA256,
            with this secret, of the timestamp, a full stop and the body (<Code>{'{timestamp}.{body}'}</Code>). Work it out
            yourself and compare before trusting the call.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

function WaitForm({ c, set }: { c: Config; set: (p: Config) => void }) {
  return (
    <Stack gap="xs">
      <Row>
        <NumberInput
          size="xs"
          label="Wait"
          min={0}
          allowDecimal={false}
          value={num(c.amount)}
          onChange={(n) => set({ amount: typeof n === 'number' ? n : 0 })}
          w={110}
          data-testid="wait-amount"
        />
        <Select
          size="xs"
          label=" "
          aria-label="Unit"
          data={UNITS}
          value={str(c.unit) || 'days'}
          allowDeselect={false}
          onChange={(x) => x && set({ unit: x })}
          w={120}
        />
        <TextInput
          size="xs"
          type="time"
          label="Then at (UK time)"
          description="Optional"
          value={str(c.at_time)}
          onChange={(e) => set({ at_time: e.currentTarget.value || undefined })}
          w={150}
          data-testid="wait-at"
        />
      </Row>
      <Text size="xs" c="dimmed">A time of day waits for the next time the UK clock reads it, after the wait.</Text>
    </Stack>
  );
}
